'use client';

import { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, parseISO, startOfWeek, endOfWeek } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X, Plus, Edit, Pencil, Clock, Utensils, Repeat, Globe, FileText, Settings, Save, Loader2 } from 'lucide-react';
import { useSchoolCalendar, useSchoolSchedules, useUpdateDaySchedule, useCancelDay, useSchool, useUpdateSchool } from '@/hooks/use-schools';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import type { SchoolViewExtra, CalendarDayInfo } from '@/types/api';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { CalendarBuilderDialog } from '@/components/calendar-builder/calendar-builder-dialog';
import { LUNCH_TYPES } from '@/config/constants';
import { useQueryClient } from '@tanstack/react-query';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SchedulePicker } from '@/components/schedule-picker';

interface SchoolOverviewTabProps {
  school: SchoolViewExtra;
  schoolId: string;
  onNavigateToSettings?: () => void;
}

export function SchoolOverviewTab({ school, schoolId, onNavigateToSettings }: SchoolOverviewTabProps) {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [showBuilderDialog, setShowBuilderDialog] = useState(false);
  const [editingDay, setEditingDay] = useState<string | null>(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [configTab, setConfigTab] = useState<'config' | 'notes'>('config');
  const queryClient = useQueryClient();
  const updateSchool = useUpdateSchool(schoolId, school?.organizationType);
  
  // Function to determine if white or black text should be used based on background color
  const getContrastColor = (hexColor: string | undefined): string => {
    if (!hexColor) return 'white';
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? 'black' : 'white';
  };
  
  // Use useSchool hook to ensure we have latest schedule settings merged (like school-schedules-tab.tsx)
  const { data: schoolWithSettings } = useSchool(schoolId);
  // Use schoolWithSettings if available, otherwise fall back to prop
  const schoolData = schoolWithSettings || school;
  
  const isCollege = schoolData?.organizationType === 'college';
  
  const {
    data: calendarData,
    isLoading: isLoadingCalendar,
    error: calendarError,
  } = useSchoolCalendar(schoolId);

  const {
    data: schedules,
    isLoading: isLoadingSchedules,
  } = useSchoolSchedules(schoolId);

  const updateDaySchedule = useUpdateDaySchedule(schoolId);
  const cancelDay = useCancelDay(schoolId);

  const handleCalendarBuilt = async () => {
    // Invalidate and refetch all related queries
    await queryClient.invalidateQueries({ queryKey: ['school', schoolId, 'calendar'] });
    await queryClient.invalidateQueries({ queryKey: ['school', schoolId, 'school_years'] });
    await queryClient.invalidateQueries({ queryKey: ['school', schoolId] });
    // Explicitly refetch school years to ensure fresh data
    await queryClient.refetchQueries({ queryKey: ['school', schoolId, 'school_years'] });
    toast.success('Calendar refreshed');
  };

  const handleScheduleChange = (date: string, scheduleId: string) => {
    updateDaySchedule.mutate({ date, scheduleId });
    setEditingDay(null);
  };

  const handleCancelDay = (date: string) => {
    cancelDay.mutate(date);
    setEditingDay(null);
  };

  const handleEditNotes = () => {
    setNotesValue(schoolData?.notes || '');
    setIsEditingNotes(true);
  };

  const handleSaveNotes = async () => {
    try {
      await updateSchool.mutateAsync({ school: { notes: notesValue } });
      setIsEditingNotes(false);
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleCancelNotesEdit = () => {
    setNotesValue(schoolData?.notes || '');
    setIsEditingNotes(false);
  };

  // Calendar helpers
  const calendarMap = new Map<string, CalendarDayInfo>();
  calendarData?.forEach((event) => {
    if (event.dateNow) {
      calendarMap.set(event.dateNow, event);
    }
  });

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const getDayInfo = (dayInfo: CalendarDayInfo | undefined) => {
    if (!dayInfo) return { bgColor: '', textColor: '', borderColor: '' };
    if (dayInfo.isCanceled) return { 
      bgColor: 'bg-red-300 dark:bg-red-800',
      textColor: 'text-red-900 dark:text-red-100',
      borderColor: 'border-red-400 dark:border-red-700'
    };
    if (!dayInfo.scheduleId) return { 
      bgColor: 'bg-red-300 dark:bg-red-800',
      textColor: 'text-red-900 dark:text-red-100',
      borderColor: 'border-red-400 dark:border-red-700'
    };
    if (dayInfo.special) return { 
      bgColor: 'bg-teal-300 dark:bg-teal-800',
      textColor: 'text-teal-900 dark:text-teal-100',
      borderColor: 'border-teal-400 dark:border-teal-700'
    };
    if (dayInfo.modified) return { 
      bgColor: 'bg-amber-300 dark:bg-amber-800',
      textColor: 'text-amber-900 dark:text-amber-100',
      borderColor: 'border-amber-400 dark:border-amber-700'
    };
    return { 
      bgColor: 'bg-blue-300 dark:bg-blue-800',
      textColor: 'text-blue-900 dark:text-blue-100',
      borderColor: 'border-blue-400 dark:border-blue-700'
    };
  };

  const schoolPrimaryColor = schoolData?.primaryColor || '#1e40af';

  // For colleges, show simplified view without calendar
  if (isCollege) {
    return (
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Configuration & Notes Card with Tabs */}
          <Card>
            <Tabs value={configTab} onValueChange={(v) => setConfigTab(v as 'config' | 'notes')} className="h-full flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <TabsList 
                    className="h-8 bg-muted/50 p-0.5"
                    style={{ 
                      '--tab-active-bg': schoolPrimaryColor,
                      '--tab-active-color': getContrastColor(schoolPrimaryColor),
                    } as React.CSSProperties}
                  >
                    <TabsTrigger 
                      value="config" 
                      className="text-xs gap-1.5 px-3 data-[state=active]:shadow-sm transition-all"
                      style={configTab === 'config' ? { 
                        backgroundColor: schoolPrimaryColor,
                        color: getContrastColor(schoolPrimaryColor),
                      } : undefined}
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Config
                    </TabsTrigger>
                    <TabsTrigger 
                      value="notes" 
                      className="text-xs gap-1.5 px-3 data-[state=active]:shadow-sm transition-all"
                      style={configTab === 'notes' ? { 
                        backgroundColor: schoolPrimaryColor,
                        color: getContrastColor(schoolPrimaryColor),
                      } : undefined}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Notes
                      {schoolData?.notes && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-current opacity-60" />}
                    </TabsTrigger>
                  </TabsList>
                  {configTab === 'notes' && !isEditingNotes && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={handleEditNotes}
                      title="Edit notes"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1">
                <TabsContent value="config" className="mt-0 space-y-4">
                  {/* Domains */}
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2.5">
                      Domains ({schoolData?.domains?.length || 0})
                    </div>
                    {schoolData?.domains && schoolData.domains.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {schoolData.domains.map((domain) => (
                          <Badge
                            key={domain}
                            variant={domain === 'joinsaturn.com' ? 'default' : 'secondary'}
                            className="text-xs px-2 py-1"
                          >
                            {domain}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">No domains</p>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="notes" className="mt-0 h-full">
                  {isEditingNotes ? (
                    <div className="flex flex-col h-full gap-3">
                      <Textarea
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        placeholder="Add notes about this school..."
                        className="flex-1 min-h-[150px] resize-none text-sm"
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelNotesEdit}
                          disabled={updateSchool.isPending}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveNotes}
                          disabled={updateSchool.isPending}
                          style={{ 
                            backgroundColor: schoolPrimaryColor,
                            color: getContrastColor(schoolPrimaryColor)
                          }}
                          className="hover:opacity-90 disabled:opacity-50"
                        >
                          {updateSchool.isPending ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-3.5 w-3.5 mr-1.5" />
                              Save
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : schoolData?.notes ? (
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground leading-relaxed">
                      {schoolData.notes}
                    </p>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full py-8 text-center">
                      <FileText className="h-10 w-10 text-muted-foreground/40 mb-3" />
                      <p className="text-sm text-muted-foreground mb-3">No notes yet</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEditNotes}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        Add Notes
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>

          {/* Location Card */}
          <Card>
            <CardHeader>
              <CardTitle>Location</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {schoolData?.timezone && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">Timezone</span>
                  <span className="text-sm font-medium">{schoolData.timezone}</span>
                </div>
              )}
              {schoolData?.state && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">State</span>
                  <span className="text-sm font-medium">{schoolData.state}</span>
                </div>
              )}
              {schoolData?.country && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">Country</span>
                  <span className="text-sm font-medium">{schoolData.country}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Grid - Calendar on Left, Configuration on Right */}
      <div className="grid gap-6 lg:grid-cols-[1fr_400px] items-start">
        {/* Left Column - Calendar */}
        <div className="space-y-4 flex flex-col">
          {/* Calendar Header */}
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={previousMonth}
              className="h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold min-w-[140px] text-center">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={nextMonth}
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={goToToday}
              className="h-8 text-xs"
            >
              Today
            </Button>
          </div>

          {/* Calendar Builder Dialog */}
          <CalendarBuilderDialog
            open={showBuilderDialog}
            onOpenChange={setShowBuilderDialog}
            school={schoolData}
            schoolId={schoolId}
            onSuccess={handleCalendarBuilt}
          />

          {/* Legend - Horizontal Row Above Calendar */}
          <div className="flex items-center gap-4 px-1">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Legend:</span>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-md bg-blue-300 dark:bg-blue-800 border border-blue-400 dark:border-blue-700 shrink-0" />
                <span className="text-xs font-medium text-blue-900 dark:text-blue-100">Normal</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-md bg-amber-300 dark:bg-amber-800 border border-amber-400 dark:border-amber-700 shrink-0" />
                <span className="text-xs font-medium text-amber-900 dark:text-amber-100">Modified</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-md bg-teal-300 dark:bg-teal-800 border border-teal-400 dark:border-teal-700 shrink-0" />
                <span className="text-xs font-medium text-teal-900 dark:text-teal-100">Special</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-md bg-red-300 dark:bg-red-800 border border-red-400 dark:border-red-700 shrink-0" />
                <span className="text-xs font-medium text-red-900 dark:text-red-100">No School</span>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <Card className="flex-1">
            <CardContent className="p-4">
              {isLoadingCalendar ? (
                <>
                  <div className="grid grid-cols-7 gap-1.5 mb-2">
                    {/* Day headers */}
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                      <div key={index} className="text-center font-semibold text-xs text-muted-foreground py-1.5">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1.5">
                    {Array.from({ length: 35 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        className="min-h-[90px] rounded-lg"
                      />
                    ))}
                  </div>
                </>
              ) : calendarError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Failed to load calendar data. Please try again.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="grid grid-cols-7 gap-1.5 mb-2">
                    {/* Day headers */}
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                      <div key={index} className="text-center font-semibold text-xs text-muted-foreground py-1.5">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1.5">
                    {/* Calendar days */}
                    {days.map((day) => {
                      const dateString = format(day, 'yyyy-MM-dd');
                      const dayInfo = calendarMap.get(dateString);
                      const isCurrentMonth = isSameMonth(day, currentMonth);
                      const isTodayDate = isToday(day);
                      const styleInfo = getDayInfo(dayInfo);
                      const isEditing = editingDay === dateString;
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6; // Sunday or Saturday
                      
                      // For weekends, render without popover
                      if (isWeekend) {
                        return (
                          <div
                            key={dateString}
                            className={`
                              min-h-[90px] p-1.5 rounded-lg
                              ${isTodayDate 
                                ? 'border-2 border-white' 
                                : (!dayInfo || !isCurrentMonth) 
                                  ? 'border border-border' 
                                  : ''
                              }
                              ${isCurrentMonth 
                                ? `${dayInfo ? styleInfo.bgColor : 'bg-card'} opacity-60`
                                : 'bg-muted/20 opacity-40'
                              }
                            `}
                          >
                            <div className="flex flex-col h-full gap-1">
                              <div className={`text-xs font-bold ${isCurrentMonth ? (dayInfo ? styleInfo.textColor : 'text-foreground') : 'text-muted-foreground'}`}>
                                {format(day, 'd')}
                              </div>
                              
                              {dayInfo && dayInfo.isCanceled && (
                                <div className="flex-1 flex items-center justify-center">
                                  <div className={`text-[10px] font-semibold leading-tight ${styleInfo.textColor}`}>
                                    No School
                                  </div>
                                </div>
                              )}
                              
                              {dayInfo && !dayInfo.isCanceled && dayInfo.scheduleDisplay && (
                                <div className="flex-1 min-w-0">
                                  <div className={`text-xs font-medium line-clamp-2 leading-tight ${styleInfo.textColor}`}>
                                    {dayInfo.scheduleDisplay}
                                  </div>
                                </div>
                              )}
                              
                              {dayInfo && !dayInfo.isCanceled && !dayInfo.scheduleDisplay && (
                                <div className="flex-1 flex items-center justify-center">
                                  <div className={`text-[10px] font-semibold leading-tight ${styleInfo.textColor}`}>
                                    Missing
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <Popover 
                          key={dateString}
                          open={isEditing}
                          onOpenChange={(open) => setEditingDay(open ? dateString : null)}
                        >
                          <PopoverTrigger asChild>
                            <div
                              className={`
                                min-h-[90px] p-1.5 rounded-lg transition-all
                                ${isTodayDate 
                                  ? 'border-2 border-white' 
                                  : (!dayInfo || !isCurrentMonth) 
                                    ? 'border border-border' 
                                    : ''
                                }
                                ${isCurrentMonth 
                                  ? `hover:shadow-md hover:scale-[1.02] cursor-pointer ${dayInfo ? styleInfo.bgColor : 'bg-card'}`
                                  : 'bg-muted/20 opacity-40'
                                }
                              `}
                            >
                              <div className="flex flex-col h-full gap-1">
                                <div className={`text-xs font-bold ${isCurrentMonth ? (dayInfo ? styleInfo.textColor : 'text-foreground') : 'text-muted-foreground'}`}>
                                  {format(day, 'd')}
                                </div>
                                
                                {dayInfo && dayInfo.isCanceled && (
                                  <div className="flex-1 flex items-center justify-center">
                                    <div className={`text-[10px] font-semibold leading-tight ${styleInfo.textColor}`}>
                                      No School
                                    </div>
                                  </div>
                                )}
                                
                                {dayInfo && !dayInfo.isCanceled && dayInfo.scheduleDisplay && (
                                  <div className="flex-1 min-w-0">
                                    <div className={`text-xs font-medium line-clamp-2 leading-tight ${styleInfo.textColor}`}>
                                      {dayInfo.scheduleDisplay}
                                    </div>
                                  </div>
                                )}
                                
                                {dayInfo && !dayInfo.isCanceled && !dayInfo.scheduleDisplay && (
                                  <div className="flex-1 flex items-center justify-center">
                                    <div className={`text-[10px] font-semibold leading-tight ${styleInfo.textColor}`}>
                                      Missing
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </PopoverTrigger>
                          {isCurrentMonth && (
                            <PopoverContent className="w-80" align="start">
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-sm">
                                    {format(day, 'EEEE, MMMM d, yyyy')}
                                  </h4>
                                  {dayInfo && (
                                    <div className="text-xs text-muted-foreground">
                                      {dayInfo.isCanceled 
                                        ? 'Day is canceled' 
                                        : dayInfo.scheduleDisplay 
                                        ? `Current: ${dayInfo.scheduleDisplay}`
                                        : 'No schedule assigned'}
                                    </div>
                                  )}
                                </div>
                                
                                <div className="space-y-2">
                                  <label className="text-sm font-medium">Change Schedule</label>
                                  {schedules && schedules.length > 0 ? (
                                    <SchedulePicker
                                      schedules={schedules}
                                      value={dayInfo?.scheduleId || ''}
                                      onValueChange={(value) => handleScheduleChange(dateString, value)}
                                      disabled={updateDaySchedule.isPending || isLoadingSchedules}
                                      placeholder="Select a schedule"
                                    />
                                  ) : (
                                    <div className="w-full py-2 px-3 text-sm text-muted-foreground border border-border rounded-md bg-muted/20">
                                      No schedules available
                                    </div>
                                  )}
                                </div>
                                
                                <div className="space-y-2">
                                  <label className="text-sm font-medium">Vacation / Break</label>
                                  <div className="grid grid-cols-2 gap-2">
                                    {[
                                      { id: '#-WINTERBREAK', label: 'Winter' },
                                      { id: '#-SPRINGBREAK', label: 'Spring' },
                                      { id: '#-SUMMERBREAK', label: 'Summer' },
                                      { id: '#-THANKSGIVING', label: 'Thanksgiving' },
                                      { id: '#-BREAK', label: 'Other Break' },
                                      { id: '#-EXAMS', label: 'Exams' },
                                    ].map((breakType) => (
                                      <Button
                                        key={breakType.id}
                                        variant={dayInfo?.dateLabel === breakType.id ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => handleScheduleChange(dateString, breakType.id)}
                                        disabled={updateDaySchedule.isPending}
                                        className="text-xs"
                                      >
                                        {breakType.label}
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                                
                                <div className="flex gap-2">
                                  {dayInfo && !dayInfo.isCanceled && (
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => handleScheduleChange(dateString, '#-BREAK')}
                                      disabled={updateDaySchedule.isPending}
                                      className="flex-1"
                                    >
                                      <X className="h-3 w-3 mr-1" />
                                      Cancel Day
                                    </Button>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEditingDay(null)}
                                    className="flex-1"
                                  >
                                    Close
                                  </Button>
                                </div>
                              </div>
                            </PopoverContent>
                          )}
                        </Popover>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Two Cards Matching Calendar Height */}
        <div className="flex flex-col gap-4 pt-[32px]">
          {/* Build Calendar Button */}
          <div className="flex justify-end flex-col items-end gap-1">
            <Button 
              style={{ 
                backgroundColor: schoolPrimaryColor,
                color: getContrastColor(schoolPrimaryColor)
              }}
              onClick={() => setShowBuilderDialog(true)}
              disabled={
                !schoolData || 
                !schoolData.rotationPattern || 
                schoolData.rotationPattern.length === 0 ||
                !schedules ||
                schedules.filter(s => s.variant === 'core').length === 0
              }
              className="hover:opacity-90 disabled:opacity-50 h-10 text-sm shrink-0"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              Build Calendar
            </Button>
            {schedules && schedules.filter(s => s.variant === 'core').length === 0 && (
              <p className="text-xs text-muted-foreground text-right">
                Create schedules first before building the calendar
              </p>
            )}
          </div>

          {/* Top Card - Configuration with Tabs */}
          <Card className="flex-1">
            <Tabs value={configTab} onValueChange={(v) => setConfigTab(v as 'config' | 'notes')} className="h-full flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <TabsList 
                    className="h-8 bg-muted/50 p-0.5"
                  >
                    <TabsTrigger 
                      value="config" 
                      className="text-xs gap-1.5 px-3 data-[state=active]:shadow-sm transition-all"
                      style={configTab === 'config' ? { 
                        backgroundColor: schoolPrimaryColor,
                        color: getContrastColor(schoolPrimaryColor),
                      } : undefined}
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Config
                    </TabsTrigger>
                    <TabsTrigger 
                      value="notes" 
                      className="text-xs gap-1.5 px-3 data-[state=active]:shadow-sm transition-all"
                      style={configTab === 'notes' ? { 
                        backgroundColor: schoolPrimaryColor,
                        color: getContrastColor(schoolPrimaryColor),
                      } : undefined}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Notes
                      {schoolData?.notes && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-current opacity-60" />}
                    </TabsTrigger>
                  </TabsList>
                  {configTab === 'config' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={onNavigateToSettings}
                      title="Edit configuration"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {configTab === 'notes' && !isEditingNotes && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={handleEditNotes}
                      title="Edit notes"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-auto">
                <TabsContent value="config" className="mt-0 space-y-4">
                  {/* Schedule Type & Lunch */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Schedule Type */}
                    {schoolData?.scheduleType && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <div 
                            className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
                            style={{ 
                              backgroundColor: `${schoolPrimaryColor}15`,
                              color: schoolPrimaryColor
                            }}
                          >
                            <CalendarIcon className="h-3.5 w-3.5" />
                          </div>
                          <span className="text-xs font-medium text-muted-foreground">Schedule Type</span>
                        </div>
                        <Badge variant="outline" className="text-xs font-semibold ml-9">
                          {schoolData.scheduleType}
                        </Badge>
                      </div>
                    )}

                    {/* Lunch Settings */}
                    {schoolData?.lunchSettings?.type && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <div 
                            className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
                            style={{ 
                              backgroundColor: `${schoolPrimaryColor}15`,
                              color: schoolPrimaryColor
                            }}
                          >
                            <Utensils className="h-3.5 w-3.5" />
                          </div>
                          <span className="text-xs font-medium text-muted-foreground">Lunch</span>
                        </div>
                        <Badge variant="outline" className="text-xs font-semibold capitalize ml-9">
                          {LUNCH_TYPES.find(lt => lt.value === schoolData.lunchSettings.type)?.label || schoolData.lunchSettings.type}
                          {schoolData.lunchSettings.type === 'waves' && 
                            ` (${schoolData.lunchSettings.slots?.length || 0})`}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Day Types */}
                  <div>
                    <div className="flex items-center gap-2 mb-2.5">
                      <div 
                        className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
                        style={{ 
                          backgroundColor: `${schoolPrimaryColor}15`,
                          color: schoolPrimaryColor
                        }}
                      >
                        <CalendarIcon className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">Day Types</span>
                    </div>
                    {schoolData?.dayTypes && schoolData.dayTypes.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 ml-9">
                        {schoolData.dayTypes.map((dayType) => (
                          <Badge 
                            key={dayType.id} 
                            variant="outline" 
                            className="text-xs font-semibold px-2.5 py-0.5"
                          >
                            {dayType.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic ml-9">No day types configured</p>
                    )}
                  </div>

                  {/* Period Types */}
                  <div>
                    <div className="flex items-center gap-2 mb-2.5">
                      <div 
                        className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
                        style={{ 
                          backgroundColor: `${schoolPrimaryColor}15`,
                          color: schoolPrimaryColor
                        }}
                      >
                        <Clock className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">Period Types</span>
                    </div>
                    {schoolData?.periodTypes && schoolData.periodTypes.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 ml-9">
                        {schoolData.periodTypes.map((periodType) => (
                          <Badge 
                            key={periodType.id} 
                            variant="outline" 
                            className="text-xs font-semibold px-2.5 py-0.5"
                          >
                            {periodType.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic ml-9">No period types configured</p>
                    )}
                  </div>

                  {/* Rotation Pattern */}
                  {schoolData?.rotationPattern && schoolData.rotationPattern.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <div className="flex items-center gap-2 mb-2.5">
                          <div 
                            className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
                            style={{ 
                              backgroundColor: `${schoolPrimaryColor}15`,
                              color: schoolPrimaryColor
                            }}
                          >
                            <Repeat className="h-3.5 w-3.5" />
                          </div>
                          <span className="text-xs font-medium text-muted-foreground">Rotation Pattern</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 ml-9">
                          {schoolData.rotationPattern.map((dayType, index) => (
                            <Badge 
                              key={index} 
                              variant="outline" 
                              className="text-xs font-mono"
                            >
                              {dayType}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Domains */}
                  {schoolData?.domains && schoolData.domains.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <div className="flex items-center gap-2 mb-2.5">
                          <div 
                            className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
                            style={{ 
                              backgroundColor: `${schoolPrimaryColor}15`,
                              color: schoolPrimaryColor
                            }}
                          >
                            <Globe className="h-3.5 w-3.5" />
                          </div>
                          <span className="text-xs font-medium text-muted-foreground">Domains</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 ml-9">
                          {schoolData.domains.map((domain) => (
                            <Badge
                              key={domain}
                              variant="outline"
                              className="text-xs px-2 py-0.5 font-medium"
                            >
                              {domain}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="notes" className="mt-0 h-full">
                  {isEditingNotes ? (
                    <div className="flex flex-col h-full gap-3">
                      <Textarea
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        placeholder="Add notes about this school..."
                        className="flex-1 min-h-[200px] resize-none text-sm"
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelNotesEdit}
                          disabled={updateSchool.isPending}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveNotes}
                          disabled={updateSchool.isPending}
                          style={{ 
                            backgroundColor: schoolPrimaryColor,
                            color: getContrastColor(schoolPrimaryColor)
                          }}
                          className="hover:opacity-90 disabled:opacity-50"
                        >
                          {updateSchool.isPending ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-3.5 w-3.5 mr-1.5" />
                              Save
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : schoolData?.notes ? (
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground leading-relaxed">
                      {schoolData.notes}
                    </p>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full py-8 text-center">
                      <FileText className="h-10 w-10 text-muted-foreground/40 mb-3" />
                      <p className="text-sm text-muted-foreground mb-3">No notes yet</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEditNotes}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        Add Notes
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>
      </div>

    </div>
  );
}

